// Purpose: AuthContext + AuthProvider + useAuth hook. One /api/me fetch on
// mount; components re-read via refresh() after known state changes.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/client.ts';
import type { AuthContextValue, AuthStatus, MeResponse } from './types.ts';

const AuthContext = createContext<AuthContextValue | null>(null);

// Public (no-auth) SPA routes. Keep in sync with App.tsx's ExternalShell
// children (/s/:token, /s/:token/files, /s/:token/legacy, /d/:token) and
// the standalone /login page. Matched at the document's initial path —
// subsequent SPA navigations don't re-run the AuthProvider mount effect,
// which is fine: going from a public route to an authenticated one
// happens via full-page OIDC redirect.
const PUBLIC_PATH_RE = /^\/(?:s|d|login)(?:\/|$)/;

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_RE.test(pathname);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<MeResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<MeResponse>('/me');
      setUser(res.data);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout', null, { baseURL: '' });
    } catch {
      // No-op: we clear local state either way so the UI matches what the
      // next /api/me fetch would tell us.
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  useEffect(() => {
    // Commit 5.6m: skip the /api/me probe on public (no-auth) routes.
    // Recipient + external-uploader pages (/s/:token/*, /d/:token/*)
    // never need a session, and the /login page has its own auth flow.
    // Hitting /api/me there (a) lights up journald with a 401 per page
    // load and (b) delays rendering while the SPA waits for the 401 to
    // land. Reading window.location here (vs useLocation) keeps this
    // out of the render path; AuthProvider mounts once per document.
    if (isPublicPath(window.location.pathname)) {
      setStatus('unauthenticated');
      return;
    }
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, refresh, signOut }),
    [status, user, refresh, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
